/**
 * Dialog Component
 *
 * Handles parsing and formatting of dialog structures.
 * Similar to component structure: contains input interface, parser, and formatter.
 */

import { v4 as uuidv4 } from 'uuid'

import { IDialog } from '@makyoapp/makyo-sdk/models/AppBuilder.js'

import { formatFromVariableText } from '../components/format-utils'
import type { AppBreakpoint } from '../components/types'
import { deepMerge, parseResponsiveFormat, parseToVariableSelect, parseToVariableText } from '../components/parser-utils'
import dialogTemplate from './dialog.template.json'

/**
 * Simplified AI Dialog Input
 */
export interface AiDialogInput {
  type: 'dialog'
  name: string
  headline: string[] // Format: ["text:Delete Item"] or ["text:Warning: ", "cr:itemName"]
  headlineTypography?: string // Format: "Title Extra Small" or responsive
  content: string[] // Format: ["text:Are you sure?"] or ["text:Delete ", "cr:itemName", "text:?"]
  contentTypography?: string // Format: "Body Large" or responsive
  showIcon?: boolean
  icon?: string // Format: "icon:warning" or "icon:info"
  showDivider?: boolean
  leadingButton?: {
    label: string // Plain text label (not variable text)
    color?: string
    enabled?: boolean
  }
  trailingButton?: {
    label: string // Plain text label (not variable text)
    color?: string
    enabled?: boolean
  }
  alignment?: {
    title?: 'start' | 'center' | 'end'
    content?: 'start' | 'center' | 'end'
    footer?: 'start' | 'center' | 'end'
    icon?: 'start' | 'center' | 'end'
  }
  width?: string // Format: "w:350px ta:w:400px de:w:530px"
}

/**
 * Parse width format string
 * Format: "<default>" or responsive: "w:350px ta:w:400px de:w:530px"
 */
function parseWidthFormat(format: string): Record<AppBreakpoint, string> {
  const parsed = parseResponsiveFormat(format, (value) => {
    // Remove "w:" prefix if present
    let cleanValue = value.replace(/^w:/, '')
    
    // If it's already a valid width string, return it
    if (cleanValue.includes('%') || cleanValue.includes('px')) {
      return cleanValue
    }
    // Default to 350px
    return '350px'
  })

  return {
    phone: parsed.phone,
    'phone-landscape': parsed['phone-landscape'],
    tablet: parsed.tablet,
    'tablet-landscape': parsed['tablet-landscape'],
    desktop: parsed.desktop,
    tv: parsed.tv,
  }
}

/**
 * Format width back to string format
 */
function formatWidth(width: Record<AppBreakpoint, string>): string | undefined {
  // Check if all values are the same
  const values = Object.values(width)
  const firstValue = values[0]
  if (values.every((v) => v === firstValue)) {
    const defaultWidth = firstValue === '90%' ? '90%' : firstValue === '350px' ? undefined : firstValue
    return defaultWidth ? `w:${defaultWidth}` : undefined
  }

  // Build responsive format string
  const parts: string[] = []
  if (width.phone !== '90%') {
    parts.push(`w:${width.phone}`)
  }
  if (width.tablet !== width.phone) {
    parts.push(`ta:w:${width.tablet}`)
  }
  if (width.desktop !== width.tablet) {
    parts.push(`de:w:${width.desktop}`)
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}

const DIALOG_DEFAULT_TEMPLATE = dialogTemplate as unknown as IDialog

/**
 * Parse AI dialog input to full dialog structure
 */
export function parseAiDialog(input: AiDialogInput): IDialog {
  // Start with template defaults
  const result = deepMerge(DIALOG_DEFAULT_TEMPLATE, {})

  // Generate new ID
  result._id = uuidv4()

  // Set name
  result._Name = input.name

  // Convert VariableTextValue arrays to JSON strings for storage
  const headlineValue = parseToVariableText(input.headline)
  result._HeadLine = JSON.stringify(headlineValue)

  const contentValue = parseToVariableText(input.content)
  result._Content = JSON.stringify(contentValue)

  // Override typography if provided
  if (input.headlineTypography) {
    result._HeadLineTypography = input.headlineTypography
  }

  if (input.contentTypography) {
    result._ContentTypography = input.contentTypography
  }

  // Override divider if provided
  if (input.showDivider !== undefined) {
    result._Divider._ShowDivider = input.showDivider
  }

  // Override icon if provided
  if (input.showIcon !== undefined) {
    result._Icon._ShowIcon = input.showIcon
  }

  if (input.icon) {
    result._Icon._Icon = parseToVariableSelect(input.icon)
  }

  // Override leading button if provided
  if (input.leadingButton) {
    if (input.leadingButton.label) {
      result._LeadingButton._Label = input.leadingButton.label
    }
    if (input.leadingButton.color) {
      result._LeadingButton._Color = input.leadingButton.color
    }
    if (input.leadingButton.enabled !== undefined) {
      result._LeadingButton._Enable = input.leadingButton.enabled
    }
  }

  // Override trailing button if provided
  if (input.trailingButton) {
    if (input.trailingButton.label) {
      result._TrailingButton._Label = input.trailingButton.label
    }
    if (input.trailingButton.color) {
      result._TrailingButton._Color = input.trailingButton.color
    }
    if (input.trailingButton.enabled !== undefined) {
      result._TrailingButton._Enabled = input.trailingButton.enabled
    }
  }

  // Override alignment if provided
  if (input.alignment) {
    if (input.alignment.title) {
      result._Alignment._Title = input.alignment.title
    }
    if (input.alignment.content) {
      result._Alignment._Content = input.alignment.content
    }
    if (input.alignment.footer) {
      result._Alignment._Footer = input.alignment.footer
    }
    if (input.alignment.icon) {
      result._Alignment._Icon = input.alignment.icon
    }
  }

  // Parse width if provided
  if (input.width) {
    result._Width = parseWidthFormat(input.width)
  }

  return result
}

/**
 * Format IDialog to AiDialogInput
 */
export function formatAiDialog(dialog: IDialog): AiDialogInput {
  // Parse headline and content from JSON strings
  let headline: string[] = []
  try {
    const headlineParsed = JSON.parse(dialog._HeadLine)
    headline = formatFromVariableText(headlineParsed)
  } catch {
    headline = typeof dialog._HeadLine === 'string' && dialog._HeadLine ? [`text:${dialog._HeadLine}`] : []
  }

  let content: string[] = []
  try {
    const contentParsed = JSON.parse(dialog._Content)
    content = formatFromVariableText(contentParsed)
  } catch {
    content = typeof dialog._Content === 'string' && dialog._Content ? [`text:${dialog._Content}`] : []
  }

  // Format icon
  const icon = dialog._Icon?._Icon?.value
  const iconFormatted = icon ? (icon.startsWith('icon:') ? icon : `icon:${icon}`) : undefined

  // Format alignment
  const alignment: AiDialogInput['alignment'] = {
    title: dialog._Alignment?._Title as 'start' | 'center' | 'end',
    content: dialog._Alignment?._Content as 'start' | 'center' | 'end',
    footer: dialog._Alignment?._Footer as 'start' | 'center' | 'end',
    icon: dialog._Alignment?._Icon as 'start' | 'center' | 'end',
  }

  // Format width
  const width = formatWidth(dialog._Width)

  const result: AiDialogInput = {
    type: 'dialog',
    name: dialog._Name,
    headline,
    content,
  }

  // Only include optional properties if they have values
  if (dialog._HeadLineTypography) {
    result.headlineTypography = dialog._HeadLineTypography
  }
  if (dialog._ContentTypography) {
    result.contentTypography = dialog._ContentTypography
  }
  if (dialog._Icon?._ShowIcon !== undefined && !dialog._Icon._ShowIcon) {
    result.showIcon = false
  }
  if (iconFormatted && iconFormatted !== 'icon:info') {
    result.icon = iconFormatted
  }
  if (dialog._Divider?._ShowDivider) {
    result.showDivider = true
  }
  if (
    dialog._LeadingButton?._Label !== 'Cancel' ||
    dialog._LeadingButton?._Color ||
    dialog._LeadingButton?._Enable === false
  ) {
    result.leadingButton = {
      label: dialog._LeadingButton?._Label || 'Cancel',
    }
    if (dialog._LeadingButton?._Color) {
      result.leadingButton.color = dialog._LeadingButton._Color
    }
    if (dialog._LeadingButton?._Enable === false) {
      result.leadingButton.enabled = false
    }
  }
  if (
    dialog._TrailingButton?._Label !== 'Confirm' ||
    dialog._TrailingButton?._Color ||
    dialog._TrailingButton?._Enabled === false
  ) {
    result.trailingButton = {
      label: dialog._TrailingButton?._Label || 'Confirm',
    }
    if (dialog._TrailingButton?._Color) {
      result.trailingButton.color = dialog._TrailingButton._Color
    }
    if (dialog._TrailingButton?._Enabled === false) {
      result.trailingButton.enabled = false
    }
  }
  if (
    alignment.title !== 'center' ||
    alignment.content !== 'center' ||
    alignment.footer !== 'center' ||
    alignment.icon !== 'center'
  ) {
    result.alignment = alignment
  }
  if (width) {
    result.width = width
  }

  return result
}
